import React, { useState, useEffect } from 'react';
import Banner from './Banner';
import {
  Container, Button, Paper, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Grid, IconButton,
  Typography, TextField, InputAdornment
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Edit, Delete, CurrencyRupee as CurrencyRupeeIcon } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx-js-style';
import { getISOWeek, getYear, format, startOfWeek, endOfWeek } from 'date-fns';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const OpenPickerIcon = CalendarMonthIcon;

// --- Denomination Calculator ---
// --- Helper: Payment Breakup Table ---


const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: '#333',
    '&:hover': {
      backgroundColor: '#444',
    },
    '&.Mui-focused': {
      backgroundColor: '#444',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#333',
      padding: '0 4px',
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#555',
    },
    '&:hover fieldset': {
      borderColor: '#777',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputBase-input': {
    color: '#fff',
    caretColor: theme.palette.primary.main,
    '&.Mui-disabled': {
      WebkitTextFillColor: '#ffffffc0',
      color: '#ffffffc0',
    },
  },
}));

const StyledSelectField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: '#333',
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#333',
      padding: '0 4px',
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#555',
    },
    '&:hover fieldset': {
      borderColor: '#777',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      color: '#ffffff',
    },
    '& .MuiSelect-icon': {
      color: '#ffffff',
    },
  },
}));

const DEFAULT_DENOMINATIONS = { 2000: '', 500: '', 200: '', 100: '', 50: '', 20: '', 10: '' };

function PaymentBreakup({ posDetails, totalSales }) {
  if (!posDetails || Object.keys(posDetails).length === 0) {
    return null;
  }

  const sortedDetails = Object.entries(posDetails).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box sx={{ my: 3, p: 2, border: '1px solid #9BEC00', borderRadius: 2 }}>
      <Typography variant="h6" sx={{ color: '#9BEC00', mb: 1, fontWeight: 'bold' }}>
        Payment Breakup
      </Typography>
      <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Mode</TableCell>
              <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDetails.map(([mode, amount]) => (
              <TableRow key={mode}>
                <TableCell sx={{ color: '#fff' }}>{mode}</TableCell>
                <TableCell align="right" sx={{ color: '#fff' }}>{`₹${Number(amount || 0).toLocaleString()}`}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#9BEC00' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: '#9BEC00' }}>{`₹${Number(totalSales || 0).toLocaleString()}`}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const renderRow = (label, formula, value, subValue, valueStyle = {}) => (
  <TableRow>
    <TableCell sx={{ color: '#fff' }}>
      {label} <Typography variant="caption" sx={{ color: '#aaa' }}>{formula}</Typography>
    </TableCell>
    <TableCell align="right" sx={{ color: '#fff', ...valueStyle }}>
      {value}
      {subValue && <Typography variant="caption" display="block" sx={{ color: '#aaa' }}>{subValue}</Typography>}
    </TableCell>
  </TableRow>
);

function DenominationCalculator({ open, onClose, onSave, initialAmount = 0 }) {
  // Only notes, no ₹5, ₹2, ₹1; add 'coins' as editable value
  const [denominations, setDenominations] = useState(DEFAULT_DENOMINATIONS);
  const [coins, setCoins] = useState('');
  useEffect(() => { if (initialAmount > 0) {
    let remaining = initialAmount;
    const sorted = Object.keys(DEFAULT_DENOMINATIONS).map(Number).sort((a, b) => b - a);
    const newD = { ...DEFAULT_DENOMINATIONS };
    for (const denom of sorted) {
      newD[denom] = Math.floor(remaining / denom);
      remaining = remaining % denom;
    }
    setDenominations(newD);
    setCoins(''); // Do not auto-set coins
  }}, [initialAmount]); // DEFAULT_DENOMINATIONS is a module constant, safe to omit from deps
  const handleChange = (d, v) => setDenominations(prev => ({ ...prev, [d]: v }));
  const handleCoinsChange = v => setCoins(v);
  const total = Object.entries(denominations).reduce((sum, [d, c]) => sum + (Number(d) * (Number(c) || 0)), 0) + Number(coins || 0);
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth 
      PaperProps={{ 
        sx: { 
          backgroundColor: '#2a2a2a', 
          borderRadius: 2, 
          border: '1px solid #F3F8FF',
          maxHeight: '90vh',
          width: '95%',
          maxWidth: '400px',
          m: 1,
          '& .MuiDialogContent-root': {
            overflowY: 'auto',
            p: 1,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#2a2a2a',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#9BEC00',
              borderRadius: '1px',
            },
          },
        } 
      }}
    >
      <DialogTitle sx={{ 
        fontSize: '1rem', 
        backgroundColor: '#2a2a2a', 
        color: '#F3F8FF', 
        borderBottom: '1px solid #444',
        p: 2,
        textAlign: 'center'
      }}>
        Cash Denomination Calculator
      </DialogTitle>
      <DialogContent sx={{ 
        backgroundColor: '#2a2a2a',
        padding: '16px 24px',
        '& .MuiTableCell-root': { 
          color: '#fff', 
          borderColor: '#444',
          padding: '8px',
          '& .MuiTableCell-head': {
            color: '#9BEC00',
            fontWeight: 'bold',
          }
        },
      }}>
        <TableContainer component={Paper} sx={{ 
        boxShadow: 'none', 
        backgroundColor: 'transparent',
        maxHeight: '60vh',
        overflow: 'auto',
        '& .MuiTableCell-root': {
          px: 1,
          py: 0.5,
        },
        '& .MuiInputBase-root': {
          minHeight: '32px',
        },
        '& .MuiInputBase-input': {
          p: '4px 8px',
          fontSize: '0.9rem',
        },
        '&::-webkit-scrollbar': {
          width: '4px',
          height: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#2a2a2a',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#9BEC00',
          borderRadius: '1px',
        },
      }}>
          <Table size="small" sx={{ '& .MuiTableCell-root': { color: '#fff', borderColor: '#444' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.85rem', padding: '8px', color: '#9BEC00 !important', fontWeight: 'bold' }}>Denomination</TableCell>
                <TableCell sx={{ fontSize: '0.85rem', padding: '8px', color: '#9BEC00 !important', fontWeight: 'bold' }}>Count</TableCell>
                <TableCell sx={{ fontSize: '0.85rem', padding: '8px', color: '#9BEC00 !important', fontWeight: 'bold' }} align="right">Total (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(denominations).sort(([a],[b])=>b-a).map(([d,c])=>(
                <TableRow key={d}>
                  <TableCell sx={{ fontSize: '0.9rem', padding: '8px', color: '#fff !important' }}>₹{d}</TableCell>
                  <TableCell sx={{ padding: '8px' }}>
                    <TextField 
                      type="number" 
                      value={c} 
                      onChange={e=>handleChange(d,e.target.value)} 
                      inputProps={{
                        min: 0,
                        style: {
                          textAlign: 'right', 
                          fontSize: '0.9rem', 
                          padding: '8px',
                          color: '#fff'
                        }
                      }} 
                      size="small" 
                      fullWidth 
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#444' },
                          '&:hover fieldset': { borderColor: '#9BEC00' },
                          '&.Mui-focused fieldset': { borderColor: '#9BEC00' },
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        },
                        '& input': { 
                          fontSize: '0.9rem', 
                          padding: '8px',
                          color: '#fff'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.9rem', padding: '8px', color: '#fff !important' }}>₹{(d*c).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontSize: '0.9rem', padding: '8px', color: '#fff !important' }}>Coins (₹)</TableCell>
                <TableCell sx={{ padding: '8px' }}>
                  <TextField 
                    type="number" 
                    value={coins} 
                    onChange={e=>handleCoinsChange(e.target.value)} 
                    inputProps={{
                      min: 0,
                      step: 1,
                      style: {
                        textAlign: 'right', 
                        fontSize: '0.9rem', 
                        padding: '8px',
                        color: '#fff'
                      }
                    }} 
                    size="small" 
                    fullWidth 
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#9BEC00' },
                        '&.Mui-focused fieldset': { borderColor: '#9BEC00' },
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      },
                      '& input': { 
                        fontSize: '0.9rem', 
                        padding: '8px',
                        color: '#fff'
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '0.9rem', padding: '8px', color: '#fff !important' }}>₹{Number(coins||0).toLocaleString()}</TableCell>
              </TableRow>
              <TableRow sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                <TableCell colSpan={2} sx={{ fontSize: '1rem', padding: '8px', fontWeight: 'bold', color: '#9BEC00 !important', borderTop: '1px solid #444' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontSize: '1rem', padding: '8px', fontWeight: 'bold', color: '#9BEC00 !important', borderTop: '1px solid #444' }}>₹{total.toLocaleString()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#2a2a2a', borderTop: '1px solid #444', padding: '16px' }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            fontSize: '0.9rem', 
            padding: '6px 16px',
            color: '#fff',
            border: '1px solid #444',
            '&:hover': {
              borderColor: '#9BEC00',
              backgroundColor: 'rgba(155, 236, 0, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={()=>{onSave(total);onClose();}} 
          variant="contained" 
          sx={{ 
            fontSize: '0.9rem', 
            padding: '6px 24px',
            backgroundColor: '#9BEC00',
            color: '#000',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#06D001'
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getWeekDateRange(week, year) {
  const jan4 = new Date(year, 0, 4);
  const start = startOfWeek(jan4, { weekStartsOn: 1 });
  start.setDate(start.getDate() + (week - 1) * 7);
  const end = endOfWeek(start, { weekStartsOn: 1 });
  return { start, end };
}

// Utility to safely render any date-like value as a string for tables
function renderDateCell(val) {
  if (!val) return '-';
  try {
    const date = val.toDate ? val.toDate() : new Date(val);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return String(val); // Return original value if date is invalid
    }
    return format(date, 'dd-MM-yyyy');
  } catch (error) {
    console.error("Error formatting date:", val, error);
    return String(val); // Fallback to original value on error
  }
}

const formatInvoiceSheet = (ws) => {
    const headerStyle = {
      fill: { fgColor: { rgb: "C6E0B4" } }, // Light Green
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (ws[address]) {
          ws[address].s = headerStyle;
        }
      }

      for (let R = 1; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: 2 }); // Amount column
        if (ws[cellAddress]) {
          ws[cellAddress].t = 'n';
          ws[cellAddress].z = '₹#,##0.00';
        }
      }
    }

    ws['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 15 }];
  };

function Settlement() {
  // ...existing code...

  // --- DatePicker/TextField style override for dark background ---
  const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
    '& .MuiInputBase-root': {
      color: '#fff',
      backgroundColor: '#333',
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: '#444',
      },
      '&.Mui-focused': {
        backgroundColor: '#444',
      },
    },
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#555',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#777',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#aaa',
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
      '&.MuiInputLabel-shrink': {
        backgroundColor: '#333',
        padding: '0 4px',
      },
    },
    '& .MuiSvgIcon-root': {
      color: '#fff',
    },
    '& .MuiPickersDay-root': {
      color: '#fff',
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: '#000',
        '&:hover': {
          backgroundColor: '#15e6b0',
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    '& .MuiPickersCalendarHeader-label': {
      color: '#fff',
    },
    '& .MuiPickersCalendarHeader-switchViewButton': {
      color: '#17ffc4',
    },
  }));



  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleBack = () => navigate('/dashboard');
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };


  const today = new Date();
  const [weekNumber, setWeekNumber] = useState(getISOWeek(today));
  const [year, setYear] = useState(getYear(today));
  const [imprestAmount, setImprestAmount] = useState('');
  const [cashInHand, setCashInHand] = useState('');
  const [showCalc, setShowCalc] = useState(false);
  const [posData, setPosData] = useState({ totalSales: 0, cashSales: 0 });
  const [invoices, setInvoices] = useState([]);
  const [invoicesNo, setInvoicesNo] = useState([]);
  const [invoicesData, setInvoicesData] = useState({ totalAmount: 0, totalAmountNo: 0 });
  const [loading, setLoading] = useState(false);
  const [lockedDataLoading, setLockedDataLoading] = useState(false);
  
  const [, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [savedSettlements, setSavedSettlements] = useState([]);
  const [lastSettlementTimestamp, setLastSettlementTimestamp] = useState(null); // For displaying the latest timestamp
  const [editingSettlement, setEditingSettlement] = useState(null); // Track if editing
  const [isLocked, setIsLocked] = useState(false); // Lock state for week/year
  const [lockedSettlementData, setLockedSettlementData] = useState(null); // Store locked settlement data
  const [newImprestAmount, setNewImprestAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState(() => {
    // Default to today in yyyy-MM-dd format
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  // Calculate cashBalance before using it in settlementAmount
  // cashBalance calculated only once before return
// const cashBalance = cashInHand - (parseFloat(imprestAmount || 0) + (posData.cashSales || 0) - (invoicesData.totalAmount || 0));

  // Settlement Amount is now Cash in Hand - New Imprest Amount
  const settlementAmount = (parseFloat(cashInHand) || 0) - (parseFloat(newImprestAmount) || 0);

  // --- Calculate Total Cash ---
  const totalCash = isLocked && lockedSettlementData
    ? (parseFloat(lockedSettlementData.imprestAmount) || 0) + (parseFloat(lockedSettlementData.posCashSales) || 0)
    : (parseFloat(imprestAmount) || 0) + (posData.cashSales || 0);

  const differenceAmount = isLocked && lockedSettlementData
    ? totalCash - (parseFloat(lockedSettlementData.invoicesAmount) || 0)
    : totalCash - (invoicesData.totalAmount || 0);

  const cashBalanceValidation = (isLocked && lockedSettlementData ? parseFloat(lockedSettlementData.cashInHand) : parseFloat(cashInHand) || 0) - differenceAmount;

  // --- Fetch POS Data from Firebase ---
  async function fetchPosData(start, end) {
    console.log('fetchPosData called with:', { start, end });
    try {
      const posSummaryRef = collection(db, 'posDailySummaries');
      const q = query(posSummaryRef);
      const querySnapshot = await getDocs(q);
      let totalSales = 0;
      let cashSales = 0;
      let details = {};
      querySnapshot.forEach(docSnap => {
        const data = docSnap.data();
        let dateVal = null;
        if (data.date && typeof data.date === 'object' && data.date.toDate) {
          dateVal = data.date.toDate();
        } else if (data.date) {
          dateVal = new Date(data.date);
        } else if (data.createdAt && data.createdAt.toDate) {
          dateVal = data.createdAt.toDate();
        }
        if (!dateVal) return;
        if (dateVal >= start && dateVal <= end) {
          totalSales += Number(data.totalSales) || 0;
          cashSales += Number(data.cashSales) || 0;
          if (data.details) {
            Object.entries(data.details).forEach(([mode, amt]) => {
              details[mode] = (details[mode] || 0) + Number(amt);
            });
          }
        }
      });
      console.log('Aggregated POS Data:', { totalSales, cashSales, details });
      setPosData({ totalSales, cashSales, details });
    } catch (error) {
      console.error('Error fetching POS summary from Firebase:', error);
      setPosData({ totalSales: 0, cashSales: 0, details: {} });
    }  
  }

  // --- Fetch Invoices Data from Firebase ---
  async function fetchInvoicesData(start, end) {
    const startStr = format(start, 'dd/MM/yyyy');
    const endStr = format(end, 'dd/MM/yyyy');
    console.log('Filtering invoices from:', startStr, 'to:', endStr);

    try {
      // Fetch ALL invoices (no where clause)
      const q = query(collection(db, 'stockEntries'));
      const querySnapshot = await getDocs(q);
      console.log('Fetched invoices count:', querySnapshot.size);
      let totalAmount = 0;
      let totalAmountNo = 0;
      const invoices = [];
      const invoicesNo = [];
      let debugIncluded = [];
      let debugExcluded = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const dateStr = data['date'];
        if (!dateStr) {
          console.warn('Invoice missing date:', data);
          return;
        }
        // Parse yyyy-MM-dd to JS Date
        const invoiceDate = new Date(dateStr);
        if (invoiceDate >= start && invoiceDate <= end) {
          const isImprestYes = (val) => {
            if (typeof val === 'string') return val.trim().toLowerCase() === 'yes';
            if (typeof val === 'boolean') return val === true;
            return false;
          };
          const isYes = isImprestYes(data.imprestSaleCash);
          const amt = parseFloat(data['amount']);
          if (isYes) {
            totalAmount += amt || 0;
            invoices.push(data);
            debugIncluded.push({id: doc.id, imprestSaleCash: data.imprestSaleCash, amount: data['amount'], parsedAmount: amt});
            console.log(`[INCLUDED] Invoice ${doc.id}: imprestSaleCash=`, data.imprestSaleCash, ', amount=', data['amount'], ', parsedAmount=', amt);
          } else {
            totalAmountNo += amt || 0;
            invoicesNo.push(data);
            debugExcluded.push({id: doc.id, imprestSaleCash: data.imprestSaleCash, amount: data['amount'], parsedAmount: amt});
            console.log(`[EXCLUDED] Invoice ${doc.id}: imprestSaleCash=`, data.imprestSaleCash, ', amount=', data['amount'], ', parsedAmount=', amt);
          }
        }
      });
      window._brewzDebugInvoices = {included: debugIncluded, excluded: debugExcluded};
      console.log(`[SUMMARY] Included invoices:`, debugIncluded);
      console.log(`[SUMMARY] Excluded invoices:`, debugExcluded);
      console.log(`[SUMMARY] Total included count:`, debugIncluded.length, ', sum:', totalAmount);
      console.log(`[SUMMARY] Total excluded count:`, debugExcluded.length, ', sum:', totalAmountNo);
      setInvoicesData({ totalAmount, totalAmountNo });
      setInvoices(invoices);
      setInvoicesNo(invoicesNo);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  // --- Load Saved Settlements ---
  async function loadSavedSettlements() {
    const q = query(collection(db, 'settlements'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const settlements = [];
    let latestTimestamp = null;
    const idPattern = /^settlement-\d{4}-\d{1,2}$/;
    querySnapshot.forEach(docSnap => {
      if (!idPattern.test(docSnap.id)) return; // Only accept correctly formatted IDs
      const data = docSnap.data();
      settlements.push({ id: docSnap.id, ...data });
      if (!latestTimestamp && data.createdAt) {
        latestTimestamp = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
      }
    });
    setSavedSettlements(settlements);
    setLastSettlementTimestamp(latestTimestamp);
  }
  // To clean up legacy entries, you can run a script to delete all documents in the settlements collection whose ID does not match /^settlement-\d{4}-\d{1,2}$/.

  // --- Save or Update Settlement ---
  async function saveSettlement() {
    if (!imprestAmount || isNaN(imprestAmount) || imprestAmount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid imprest amount', severity: 'error' });
      return;
    }
    if (!newImprestAmount || isNaN(newImprestAmount) || newImprestAmount < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid New Imprest Amount', severity: 'error' });
      return;
    }
    if (!settlementDate) {
      setSnackbar({ open: true, message: 'Please select a settlement date', severity: 'error' });
      return;
    }
    const { start, end } = getWeekDateRange(weekNumber, year);
    const settlementId = `settlement-${year}-${weekNumber}`;
    // Calculate only the uppercase-labeled, non-duplicate values
    // --- Calculate all derived values at save time to ensure data integrity ---
    const imprestValue = parseFloat(imprestAmount || 0);
    const cashSalesValue = posData.cashSales || 0;
    const invoicesValue = invoicesData.totalAmount || 0;
    const invoicesValueNo = invoicesData.totalAmountNo || 0;
    const cashInHandValue = cashInHand || 0;
    const newImprestValue = parseFloat(newImprestAmount || 0);

    const totalCash = imprestValue + cashSalesValue;
    const calculatedDifference = totalCash - invoicesValue;
    const calculatedCashBalance = cashInHandValue - calculatedDifference;
    const calculatedSettlementAmount = cashInHandValue - newImprestValue;

    const settlement = {
      weekNumber, year, startDate: start, endDate: end,
      imprestAmount: imprestValue,
      posCashSales: cashSalesValue,
      totalCash: totalCash,
      invoicesAmount: invoicesValue,
      invoicesAmountNo: invoicesValueNo,
      differenceAmount: calculatedDifference,
      cashInHand: cashInHandValue,
      cashBalanceValidation: calculatedCashBalance,
      shortageOrExcessAmount: calculatedCashBalance, // This is the same as the validation amount
      newImprestAmount: newImprestValue,
      settlementAmount: calculatedSettlementAmount,
      settlementDate,
      createdAt: new Date()
    };

    setLoading(true);
    // Always use setDoc with the unique settlement ID for both new and edited settlements
    if (!editingSettlement) {
      const existingDoc = await getDoc(doc(db, 'settlements', settlementId));
      if (existingDoc.exists()) {
        setSnackbar({ open: true, message: `Settlement for week ${weekNumber} of ${year} already exists.`, severity: 'error' });
        setLoading(false);
        return;
      }
    }
    await setDoc(doc(db, 'settlements', settlementId), settlement);
    setSnackbar({ open: true, message: editingSettlement ? 'Settlement updated successfully' : 'Settlement saved successfully', severity: 'success' });
    setEditingSettlement(null);
    setLoading(false);
    loadSavedSettlements();
  }

  // --- Edit Settlement ---
  function handleEditSettlement(settlement) {
    setWeekNumber(settlement.weekNumber);
    setYear(settlement.year);
    setImprestAmount(settlement.imprestAmount);
    setNewImprestAmount(settlement.newImprestAmount);
    setCashInHand(settlement.cashInHand);
    setSettlementDate(settlement.settlementDate ? (typeof settlement.settlementDate === 'string' ? settlement.settlementDate : (settlement.settlementDate.toDate ? settlement.settlementDate.toDate().toISOString().split('T')[0] : '')) : '');
    setEditingSettlement(settlement);
    setSnackbar({ open: true, message: 'Editing settlement. Save to update.', severity: 'info' });
  }

  // --- Delete Settlement ---
  async function handleDeleteSettlement(settlement) {
    if (!window.confirm('Are you sure you want to delete this settlement?')) return;
    setLoading(true);
    const settlementId = `settlement-${settlement.year}-${settlement.weekNumber}`;
    await deleteDoc(doc(db, 'settlements', settlementId));
    setSnackbar({ open: true, message: 'Settlement deleted.', severity: 'success' });
    setLoading(false);
    loadSavedSettlements();
  }

  // --- Export to Excel ---
  function exportToExcel() {
    const { start, end } = getWeekDateRange(weekNumber, year);
    const posTypes = posData.details ? Object.keys(posData.details) : [];
    const headers = [
      'Week Number', 'Year', 'Start Date', 'End Date', 'Imprest Amount', 'Imprest Yes', 'Imprest No', 'POS Total Sales', 'POS Cash Sales',
      ...posTypes,
      'Difference Amount', 'Cash in Hand', 'Cash Balance Validation', 'New Imprest Amount', 'Settlement Amount', 'Settlement Date'
    ];

    const dataForExport = isLocked && lockedSettlementData
      ? {
          ...lockedSettlementData,
          posTotalSales: posData.totalSales,
          posCashSales: posData.cashSales,
          invoicesAmount: invoicesData.totalAmount,
          invoicesAmountNo: invoicesData.totalAmountNo,
          differenceAmount: parseFloat(lockedSettlementData.imprestAmount || 0) + (posData.cashSales || 0) - (invoicesData.totalAmount || 0),
          cashBalanceValidation: (lockedSettlementData.cashInHand || 0) - (parseFloat(lockedSettlementData.imprestAmount || 0) + (posData.cashSales || 0) - (invoicesData.totalAmount || 0)),
        }
      : {
        imprestAmount,
        invoicesAmount: invoicesData.totalAmount,
        invoicesAmountNo: invoicesData.totalAmountNo,
        posTotalSales: posData.totalSales,
        posCashSales: posData.cashSales,
        differenceAmount: parseFloat(imprestAmount || 0) + (posData.cashSales || 0) - (invoicesData.totalAmount || 0),
        cashInHand,
        cashBalanceValidation: cashBalance,
        newImprestAmount,
        settlementAmount,
        settlementDate
      };

    const values = [
      weekNumber,
      year,
      format(start, 'dd/MM/yyyy'),
      format(end, 'dd/MM/yyyy'),
      dataForExport.imprestAmount,
      dataForExport.invoicesAmount, // Imprest Yes
      dataForExport.invoicesAmountNo, // Imprest No
      dataForExport.posTotalSales,
      dataForExport.posCashSales,
      ...posTypes.map(ptype => posData.details && posData.details[ptype] !== undefined ? Number(posData.details[ptype]) : ''),
      dataForExport.differenceAmount,
      dataForExport.cashInHand,
      dataForExport.cashBalanceValidation,
      dataForExport.newImprestAmount,
      dataForExport.settlementAmount,
      renderDateCell(dataForExport.settlementDate)
    ];

    const dataForSheet = [headers, values];
    const ws = XLSX.utils.aoa_to_sheet(dataForSheet);

    const headerStyle = { 
      fill: { fgColor: { rgb: "C6E0B4" } },
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ c: C, r: 0 });
        if (ws[address]) ws[address].s = headerStyle;
      }
    }

    const colWidths = headers.map((h, i) => ({ wch: Math.max(h.length, (values[i] || "").toString().length) + 2 }));
    ws['!cols'] = colWidths;

    const currencyColumns = [];
    headers.forEach((header, index) => {
      if (header.toLowerCase().includes('amount') || header.toLowerCase().includes('sales') || header.toLowerCase().includes('imprest') || header.toLowerCase().includes('cash')) {
        currencyColumns.push(index);
      }
    });

    currencyColumns.forEach(colIndex => {
      const cellAddress = XLSX.utils.encode_cell({ r: 1, c: colIndex });
      if (ws[cellAddress] && typeof ws[cellAddress].v === 'number') {
        ws[cellAddress].t = 'n';
        ws[cellAddress].z = '₹#,##0.00';
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Settlement');

    const invoicesYes = invoices.filter(inv => inv.imprestSaleCash !== 'No');
    if (invoicesYes.length > 0) {
      const invoicesHeader = ["Date", "Vendor", "Amount", "Imprest Sale", "Image URL"];
      const invoicesData = invoicesYes.map(inv => [renderDateCell(inv.date), inv.vendorName, inv.amount, 'Yes', inv.imageUrl || 'N/A']);
      const wsInvoices = XLSX.utils.aoa_to_sheet([invoicesHeader, ...invoicesData]);
      formatInvoiceSheet(wsInvoices);
      XLSX.utils.book_append_sheet(wb, wsInvoices, 'Settlement Invoices');
    }

    const invoicesNoOnly = invoicesNo.filter(inv => inv.imprestSaleCash === 'No');
    if (invoicesNoOnly.length > 0) {
      const invoicesNoHeader = ["Date", "Vendor", "Amount", "Imprest Sale", "Image URL"];
      const invoicesNoData = invoicesNoOnly.map(inv => [renderDateCell(inv.date), inv.vendorName, inv.amount, 'No', inv.imageUrl || 'N/A']);
      const wsInvoicesNo = XLSX.utils.aoa_to_sheet([invoicesNoHeader, ...invoicesNoData]);
      formatInvoiceSheet(wsInvoicesNo);
      XLSX.utils.book_append_sheet(wb, wsInvoicesNo, 'Non-Settlement Invoices');
    }

    XLSX.writeFile(wb, `Settlement_Week_${weekNumber}_${year}.xlsx`);
  }

  useEffect(() => {
    const { start, end } = getWeekDateRange(weekNumber, year);
    fetchPosData(start, end);
    fetchInvoicesData(start, end);

    const checkLock = async () => {
      setLockedDataLoading(true);
      const settlementId = `settlement-${year}-${weekNumber}`;
      const docSnap = await getDoc(doc(db, 'settlements', settlementId));
      if (docSnap.exists() && !editingSettlement) {
        setIsLocked(true);
        const data = docSnap.data();
        setLockedSettlementData(data);
      } else {
        setIsLocked(false);
        setLockedSettlementData(null);
      }
      setLockedDataLoading(false);
    };

    checkLock();
  }, [weekNumber, year, editingSettlement]);

  // --- Load latest settlement timestamp on mount and after saving
  useEffect(() => {
    loadSavedSettlements();
  }, []);

  const { start, end } = getWeekDateRange(weekNumber, year);

  // Calculate cash balance just before return
  const cashBalance = (isLocked && lockedSettlementData ? lockedSettlementData.cashInHand : cashInHand) - ((parseFloat(imprestAmount || 0) + (posData.cashSales || 0)) - (invoicesData.totalAmount || 0));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', color: '#fff' }}>
      <Banner title="Brewz Settlement" handleBack={handleBack} handleLogout={handleLogout} />
      <Container maxWidth="lg" sx={{ flexGrow: 1, padding: '20px', color: '#fff' }}>
        {lastSettlementTimestamp && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="#9BEC00" sx={{ backgroundColor: '#333', borderRadius: 1, px: 1 }}>Last saved: {renderDateCell(lastSettlementTimestamp)}</Typography>
            </Box>
          )}
        {lockedDataLoading && !lockedSettlementData ? (
          <Box sx={{ mb: 2, p: 2, background: '#fff3cd', color: '#856404', borderRadius: 2, textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
            Loading locked settlement data...
          </Box>
        ) : null}
        {isLocked && lockedSettlementData ? (
           <Box
             sx={{
               mb: 2,
               p: 0,
               borderRadius: 2,
               textAlign: 'center',
               fontWeight: 'bold',
               fontSize: '1.1em',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               minHeight: 180,
               backgroundImage: `url(${require('../locked.jpg')})`,
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               color: '#fff',
               position: 'relative',
               overflow: 'hidden',
             }}
           >
             <Box
               sx={{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 bgcolor: 'rgba(30,30,30,0.55)',
                 zIndex: 1,
                 borderRadius: 2,
               }}
             />
             <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
               <span style={{ fontSize: 32, marginBottom: 8, filter: 'drop-shadow(0 1px 6px #000)' }}>🔒</span>
               <Typography variant="h6" sx={{ color: '#fff', textShadow: '0 1px 8px #000', mb: 1 }}>
                 Settlement for Week {lockedSettlementData.weekNumber} - {lockedSettlementData.year}
               </Typography>
               <Typography variant="subtitle1" sx={{ color: '#fff', textShadow: '0 1px 8px #000' }}>
                 Saved & Locked
               </Typography>
             </Box>
           </Box>
         ) : null}
        <Paper sx={{ width: '100%', backgroundColor: 'rgba(42, 42, 42, 0.5)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff', p: { xs: 2, sm: 4 }, mb: 4, borderRadius: 3, boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)' }}>
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <StyledSelectField select label="Week Number" value={weekNumber} onChange={e => setWeekNumber(Number(e.target.value))} fullWidth>
                {[...Array(53).keys()].map(w => <MenuItem key={w + 1} value={w + 1}>{w + 1}</MenuItem>)}
              </StyledSelectField>
            </Grid>
            <Grid xs={12} md={6}>
              <StyledSelectField select label="Year" value={year} onChange={e => setYear(Number(e.target.value))} fullWidth>
                {[...Array(5).keys()].map(i => <MenuItem key={getYear(today) - i} value={getYear(today) - i}>{getYear(today) - i}</MenuItem>)}
              </StyledSelectField>
            </Grid>
            <Grid xs={12}>
              <Box sx={{ p: 2, border: '1px solid #9BEC00', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: '#9BEC00', fontWeight: 'bold' }}>
                  {`${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')}`}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <PaymentBreakup posDetails={posData.details} totalSales={posData.totalSales} />

          <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none', mt: 2 }}>
            <Table>
              <TableBody>
                {renderRow("Imprest Amount", "(A)", <StyledTextField type="number" value={isLocked ? (lockedSettlementData.imprestAmount || '0') : imprestAmount} onChange={e => setImprestAmount(e.target.value)} disabled={isLocked} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />, null, { textAlign: 'right' })}
                {renderRow("POS Cash Sales", "(B)", `₹${(isLocked && lockedSettlementData ? lockedSettlementData.posCashSales : posData.cashSales || 0).toLocaleString()}`)}
                {renderRow("Total Cash", "(A+B)", `₹${totalCash.toLocaleString()}`, null, { fontWeight: 'bold', color: '#9BEC00' })}
                {renderRow("Invoices Amount", "(D)", `- ₹${(isLocked && lockedSettlementData ? lockedSettlementData.invoicesAmount : invoicesData.totalAmount || 0).toLocaleString()}`)}
                {renderRow("Difference Amount", "(C-D)", `₹${differenceAmount.toLocaleString()}`, `(${totalCash.toLocaleString()} - ${invoicesData.totalAmount.toLocaleString()})`)}
                {renderRow("Cash in Hand", "(F)", 
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <IconButton onClick={() => setShowCalc(true)} sx={{ color: '#9BEC00', backgroundColor: 'rgba(155, 236, 0, 0.2)', '&:hover': { backgroundColor: 'rgba(155, 236, 0, 0.4)'}, mr: 1}}><CurrencyRupeeIcon /></IconButton>
                    <StyledTextField type="number" value={isLocked ? (lockedSettlementData.cashInHand || '0') : cashInHand} onChange={e => setCashInHand(e.target.value)} disabled={isLocked} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  </Box>
                , null, { textAlign: 'right' })}
                {renderRow("Cash Balance Validation", "(F-E)", `₹${cashBalanceValidation.toLocaleString()}`, `(${cashInHand.toLocaleString()} - ${differenceAmount.toLocaleString()})`, { color: cashBalanceValidation < 0 ? '#FF6B6B' : '#9BEC00' })}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'space-around' }}>
            <Grid xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker label="Settlement Date" value={settlementDate ? dayjs(settlementDate) : null} onChange={(date) => setSettlementDate(date ? date.toDate() : null)} disabled={isLocked} slots={{ openPickerIcon: OpenPickerIcon }} slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }} />
              </LocalizationProvider>
            </Grid>
            <Grid xs={6}>
              <StyledTextField label="New Imprest Amount" type="number" value={isLocked ? (lockedSettlementData.newImprestAmount || '0') : newImprestAmount} onChange={e => setNewImprestAmount(e.target.value)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} disabled={isLocked} />
            </Grid>
            <Grid xs={6}>
              <StyledTextField label="Settlement Amount" value={`₹${(isLocked && lockedSettlementData ? lockedSettlementData.settlementAmount : settlementAmount).toLocaleString()}`} InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start"></InputAdornment> }} fullWidth disabled helperText="Cash in Hand - New Imprest" FormHelperTextProps={{ sx: { color: '#aaa' } }} />
            </Grid>
          </Grid>

          {/* Action Buttons below the grid */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" sx={{ backgroundColor: '#9BEC00', color: '#000', fontWeight: 'bold', px: 5, py: 1.5, '&:hover': { backgroundColor: '#06D001' } }} onClick={saveSettlement} disabled={isLocked || loading}>
              {editingSettlement ? 'Update Settlement' : 'Save Settlement'}
            </Button>
          </Box>
          <DenominationCalculator open={showCalc} onClose={() => setShowCalc(false)} onSave={(total) => setCashInHand(total.toString())} initialAmount={Number(cashInHand) || 0} />
        </Paper>
      </Container>

      <Container maxWidth="lg" sx={{ flexGrow: 1, padding: '20px' }}>
        <Paper sx={{ backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #444', p: { xs: 2, sm: 4 }, mb: 4, borderRadius: 3, boxShadow: 4 }}>
          {(invoices.length > 0 || invoicesNo.length > 0) && (
            <Box>
              <Typography variant="h6" sx={{ color: '#9BEC00', fontWeight: 'bold' }}>Invoices for this week</Typography>
              <TableContainer component={Paper} sx={{ my: 2, maxHeight: 300, backgroundColor: '#2a2a2a', borderRadius: 2, border: '1px solid #444' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Imprest Sale</TableCell>
                      <TableCell>Image</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...(invoices || []), ...(invoicesNo || [])]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((invoice, index) => (
                        <TableRow key={`${invoice.id}-${index}`}>
                          <TableCell>{renderDateCell(invoice.date)}</TableCell>
                          <TableCell>{invoice.vendorName}</TableCell>
                          <TableCell align="right">{`₹${invoice.amount.toLocaleString()}`}</TableCell>
                          <TableCell>{invoice.imprestSaleCash === 'No' ? 'No' : 'Yes'}</TableCell>
                          <TableCell>{invoice.imageUrl ? (
                            <a href={invoice.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#17ffc4' }}>View Image</a>
                          ) : 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Button 
            variant="contained" 
            onClick={exportToExcel}
            sx={{ 
              backgroundColor: '#06D001',
              color: '#000',
              '&:hover': { 
                backgroundColor: '#9BEC00'
              },
              fontWeight: 'bold',
              mt: 2
            }}
          >
            Download to Excel
          </Button>
        </Paper>

        <Paper sx={{ backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #444', p: { xs: 2, sm: 4 }, mb: 4, borderRadius: 3, boxShadow: 4 }}>
          <Typography variant="h6"  sx={{ color: '#9BEC00', fontWeight: 'bold' }}>Stored Settlements</Typography>
          <TableContainer 
            component={Paper} 
            sx={{ 
              my: 2, 
              backgroundColor: '#2a2a2a', 
              borderRadius: 2, 
              border: '1px solid #444',
              overflowX: 'hidden',
            }}
          >
            <Table 
              stickyHeader
              size="small"
              sx={{ 
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { 
                  color: '#fff', 
                  borderColor: '#444',
                  padding: { xs: '4px 6px', sm: '6px 8px', md: '8px 10px' },
                  fontSize: { xs: '0.68rem', sm: '0.75rem', md: '0.8rem' },
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.25,
                },
                '& .MuiTableHead .MuiTableCell-root': {
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  backgroundColor: '#1e1e1e',
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '8%' }}>Year-Week</TableCell>
                  <TableCell sx={{ width: '10%' }}>P.Imprest Amount</TableCell>
                  <TableCell sx={{ width: '10%' }}>Imprest Invoices</TableCell>
                  <TableCell sx={{ width: '10%' }}>Non-Imprest Invoices</TableCell>
                  <TableCell sx={{ width: '10%' }}>POS Cash</TableCell>
                  <TableCell sx={{ width: '10%' }}>Cash in Hand</TableCell>
                  <TableCell sx={{ width: '10%' }}>Cash Balance</TableCell>
                  <TableCell sx={{ width: '10%' }}>New Imprest Amount</TableCell>
                  <TableCell sx={{ width: '10%' }}>Settlement Amount</TableCell>
                  <TableCell sx={{ width: '10%' }}>Date</TableCell>
                  <TableCell sx={{ width: '5%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...savedSettlements]
                  .sort((a, b) => {
                    const yearA = Number(a?.year) || 0;
                    const yearB = Number(b?.year) || 0;
                    if (yearB !== yearA) return yearB - yearA;
                    const weekA = Number(a?.weekNumber) || 0;
                    const weekB = Number(b?.weekNumber) || 0;
                    return weekB - weekA;
                  })
                  .map((s) => (
                  <TableRow key={s.id || `${s.year}-${s.weekNumber}`}>
                    <TableCell>{`${s.year}-W${String(s.weekNumber).padStart(2, '0')}`}</TableCell>
                    <TableCell>{s.imprestAmount !== undefined ? `₹${Number(s.imprestAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.invoicesAmount !== undefined ? `₹${Number(s.invoicesAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.invoicesAmountNo !== undefined ? `₹${Number(s.invoicesAmountNo).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.posCashSales !== undefined ? `₹${Number(s.posCashSales).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.cashInHand !== undefined ? `₹${Number(s.cashInHand).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.cashBalanceValidation !== undefined ? `₹${Number(s.cashBalanceValidation).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.newImprestAmount !== undefined ? `₹${Number(s.newImprestAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{s.settlementAmount !== undefined ? `₹${Number(s.settlementAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}</TableCell>
                    <TableCell>{renderDateCell(s.settlementDate)}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditSettlement(s)} sx={{ color: '#17ffc4', padding: '4px' }}>
                        <Edit sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteSettlement(s)} sx={{ color: '#ff5252', padding: '4px' }}>
                        <Delete sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
    </Container>
        <Container maxWidth="md" sx={{ flexGrow: 1, padding: '20px' }}>
        <DenominationCalculator open={showCalc} onClose={() => setShowCalc(false)} onSave={setCashInHand} initialAmount={cashInHand} />
      </Container>
    </Box>
  );
}

export default Settlement;